/* eslint-disable react-refresh/only-export-components */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";

import { getMeApi } from "$/api/get-me";

import { useSelectedYear } from "../hooks/useSelectedYear";
import type { UserType } from "../types/models/User.types";
import { QueryKeys } from "../utils/constants/queryKeys.constants";
import { trackResolvedAuthState } from "../utils/functions/gtm.functions";

type AuthContextType = {
  user: UserType | null;
  isFetching: boolean;
  status: "pending" | "error" | "success";
  setUser: (user: UserType) => void;
  invalidateUser: (fetchingStateVisible?: boolean) => Promise<void>;
  clearUser: () => void;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  isFetching: true,
  status: "pending",
  setUser: () => {},
  invalidateUser: async () => {},
  clearUser: () => {},
});

export default function AuthProvider({ children }: PropsWithChildren) {
  const [showFetchingState, setShowFetchingState] = useState(true);

  const queryClient = useQueryClient();

  const { sessionStorageYearValue: selectedYear } = useSelectedYear();

  const {
    data: user,
    isFetching,
    status,
  } = useQuery({
    queryKey: [QueryKeys.getUsers, selectedYear],
    queryFn: () => getMeApi(selectedYear),
    retry: false,
  });

  const setUser = (data: UserType) => {
    queryClient.setQueryData([QueryKeys.getUsers, selectedYear], data);
  };

  const invalidateUser = async (fetchingStateVisible?: boolean) => {
    setShowFetchingState(!!fetchingStateVisible);
    await queryClient.invalidateQueries({
      queryKey: [QueryKeys.getUsers, selectedYear],
    });
    setShowFetchingState(true);
  };

  const clearUser = () => {
    queryClient.setQueryData([QueryKeys.getUsers, selectedYear], null);
  };

  useEffect(() => {
    if (isFetching || status === "pending") return;

    trackResolvedAuthState({
      isAuthenticated: Boolean(user),
      userId: user?.id,
    });
  }, [isFetching, status, user]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isFetching: isFetching && showFetchingState,
        status,
        setUser,
        clearUser,
        invalidateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export type VerifiedAuthContext = {
  user: UserType;
  isFetching: boolean;
  status: "success";
  setUser: (user: UserType) => void;
  invalidateUser: (fetchingStateVisible?: boolean) => Promise<void>;
  clearUser: () => void;
};
